import { Component, Inject, ViewChild } from '@angular/core'
import { MapService } from 'src/app/core/services/map.service'
import { Observable, Subject, debounceTime, interval, takeUntil } from 'rxjs'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { AppPaths } from 'src/app/app-routing.module'
import { Layers } from 'src/app/shared/layers-config/layers'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { Permissions } from 'src/app/core/enums/permissions.enum'
import { LayerRolloutsTileService } from 'src/app/shared/layers/layer-rollouts-map-tile.service'
import { LayerRolloutsMatchedSignsTileService } from 'src/app/shared/layers/layer-rollouts-matched-sign.service'
import { DataRolloutTilesService } from 'src/app/shared/services/data-rollout-tiles-service'
import { ProviderInfo } from 'src/app/shared/models/provider-info.model'
import { Datasources } from 'src/app/shared/enums/datasources.enum'

@Component({
  selector: 'app-customer-page',
  templateUrl: './customer-page.component.html',
  providers: [
    LayerRolloutsTileService,
    LayerRolloutsMatchedSignsTileService,
    DataRolloutTilesService,
  ],
  standalone: false,
})
export class CustomerPageComponent {
  public Permissions = Permissions
  public appPath = AppPaths.Customer
  private stopTimer = new Subject<void>()
  public isInLiveMode = false
  public autoRefreshIntervalInMs = 0
  public layerIds = Layers

  public currentTimestamp: Date | undefined
  public currentTimespan: number | undefined
  public isHistoryVisible = false
  public isLegendVisible = false
  public isReportingModeActive = false
  public ProviderInfo$!: Observable<ProviderInfo>
  public allowedDatasources: Datasources[] = []
  public currentDatasource: string = ''

  constructor(
    private mapService: MapService,
    public appState: AppStateService,
    private LayerRolloutsTileService: LayerRolloutsTileService,
    private LayerRolloutsMatchedSignsTileService: LayerRolloutsMatchedSignsTileService,
  ) {
    this.mapService.activateGeoLocationSearch()
    this.mapService.setPosition(
      this.appState.getState().lat,
      this.appState.getState().lon,
      this.appState.getState().zoom,
    )
    if (this.appState.getState().historyEnabled) {
      this.isHistoryVisible = true
    }
    this.updateQueryParameters()
    this.mapService.mapViewChanged
      .pipe(takeUntilDestroyed())
      .pipe(debounceTime(120))
      .subscribe(() => {
        this.updateQueryParameters()
      })

    this.evaluateQueryParameters()
    this.appState.stateChanged.pipe(takeUntilDestroyed()).subscribe(() => {
      this.evaluateQueryParameters()
    })
  }

  ngOnDestroy() {
    this.mapService.deactivateGeoLocationSearch()
    this.stopTimer.next()
    this.stopTimer.complete()
  }

  setLiveModeRefreshInterval(seconds: number) {
    this.autoRefreshIntervalInMs = seconds * 1000
    this.stopLiveMode()
    if (this.autoRefreshIntervalInMs > 0) {
      this.startLiveMode()
    }
  }

  startLiveMode() {
    this.isInLiveMode = true
    interval(this.autoRefreshIntervalInMs)
      .pipe(takeUntil(this.stopTimer))
      .subscribe(() => {})
  }

  stopLiveMode() {
    this.isInLiveMode = false
    this.stopTimer.next()
  }

  toggleHistory = () => {
    this.isHistoryVisible = !this.isHistoryVisible
    if (this.isHistoryVisible) {
      this.isReportingModeActive = false
    }
    this.updateQueryParameters()
  }

  toggleLegend = () => {
    this.isLegendVisible = !this.isLegendVisible
  }

  toggleReportingMode = () => {
    this.isReportingModeActive = !this.isReportingModeActive
  }

  handleHistoryClosed = () => {
    this.isHistoryVisible = false
    this.updateQueryParameters()
  }

  evaluateQueryParameters() {
    if (this.appState.getState().historyEnabled) {
      this.isHistoryVisible = true
    }
  }

  updateQueryParameters() {
    this.appState.setState(AppPaths.Customer, {
      lat: this.mapService.map.getCenter().lat,
      lon: this.mapService.map.getCenter().lng,
      zoom: this.mapService.map.getZoom(),
      historyEnabled: this.isHistoryVisible ? true : undefined,
    })
  }
}
